// Stock projection waterfall v2: the Plotly figure for one replenishment site,
// built from the walk (stockWaterfall.walk.ts). Plain trace / layout / shape
// objects; the view hands them to react-plotly.js.
import type { Dash, Data, Layout, PlotData, PlotMarker, Shape } from 'plotly.js'
import {
  differenceInCalendarDays,
  format,
  isValid,
  parse,
  subWeeks,
} from 'date-fns'
import {
  SeriesDef,
  SiteWalk,
  SERIES,
  STEPS,
  WeekWalk,
} from './stockWaterfall.walk'
import {
  CHART_COLOURS,
  FAMILY_COLOURS,
  SAFETY_STOCK_COLOUR,
} from './StockWaterfall.constants'

const BAR_WIDTH = 0.72
const FONT = 'proxima-nova'
// The walk keeps zero-valued segments; neither the bars nor the hover show them.
const EPSILON = 1e-6

const numberFormat = new Intl.NumberFormat('en-GB', {
  maximumFractionDigits: 0,
})
// A bar's `base` (where each segment starts) is Plotly's, not in its types.
type BarTrace = Partial<PlotData> & { base: (number | null)[] }

export const formatQuantity = (v: number) => numberFormat.format(v)
export const formatSigned = (v: number) =>
  (v > 0 ? '+' : v < 0 ? '−' : '') + numberFormat.format(Math.abs(v))

const dateOf = (dateKey: number) =>
  parse(String(dateKey), 'yyyyMMdd', new Date())

// The plan row's planStartWeek ('yyyy-MM-dd'), or null when absent or unreadable.
export const parsePlanStart = (planStartWeek?: string | null): Date | null => {
  if (!planStartWeek) return null
  const date = parse(planStartWeek.slice(0, 10), 'yyyy-MM-dd', new Date())
  return isValid(date) ? date : null
}

// Plan week 1 is the plan's start week, counting back through 0 before it, as
// SNP's "Week NN" labels (Classic's tooltip).
const planWeek = (date: Date, planStart: Date) =>
  Math.round(differenceInCalendarDays(date, planStart) / 7) + 1

// An outer-level hover line: the label bold, the value not.
const line = (label: string, value: string) => `<b>${label}:</b> ${value}`
// A hover line whose figure is bold too: the stock levels, step totals and the
// week of production.
const boldLine = (label: string, value: string) => `<b>${label}: ${value}</b>`

// One hover box for the whole week (US154348), the same whichever step is
// hovered: the week, opening stock, one line per step with a non-zero series
// (its series inline, so a week is at most nine lines and fits the 200 px
// multiples, US154407), closing stock, the targets and the week of production
// (the arrival week less the deployment lead time, as Classic; only when the
// lead time is known).
export const weekHoverText = (week: WeekWalk, planStart: Date | null) => {
  const date = dateOf(week.dateKey)
  const heading = [
    planStart ? `Plan week ${planWeek(date, planStart)}` : null,
    `w/c ${format(date, 'EEE dd MMM yyyy')}`,
    `ISO week ${Number(week.yearIsoWeek.slice(4))}`,
  ]
  const lines = [
    `<b>${heading.filter(Boolean).join(' · ')}</b>`,
    boldLine('Opening stock', formatQuantity(week.opening)),
  ]
  week.steps.forEach((step) => {
    const segments = step.segments.filter((s) => s.high - s.low > EPSILON)
    if (!segments.length) return
    const series = segments
      .map((s) => `${s.series.short}: ${formatSigned(s.value)}`)
      .join(' · ')
    lines.push(
      `${boldLine(step.step.label, formatSigned(step.end - step.start))} (${series})`
    )
  })
  lines.push(
    boldLine('Closing stock', formatQuantity(week.closing)),
    line(
      'Targets',
      `safety ${formatQuantity(week.safetyStock)} · end-of-week ${formatQuantity(week.targetClosing)}`
    )
  )
  if (week.productionLeadWeeks !== null) {
    const produced = subWeeks(date, week.productionLeadWeeks)
    lines.push(
      boldLine('Week of production', `w/c ${format(produced, 'dd MMM yyyy')}`)
    )
  }
  return lines.join('<br>')
}

export const rgba = (hex: string, alpha: number) => {
  const n = parseInt(hex.replace('#', ''), 16)
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`
}

// Fill carries firmness, the same way in every family (§2.3).
export const markerFor = (series: SeriesDef): Partial<PlotMarker> => {
  const hue = FAMILY_COLOURS[series.family]
  const outline = { color: hue, width: 1.2 }
  if (series.fill === 'hatched') {
    return {
      color: CHART_COLOURS.surface,
      pattern: {
        shape: '/',
        fgcolor: hue,
        bgcolor: CHART_COLOURS.surface,
        size: 6,
        solidity: 0.32,
      },
      line: outline,
    }
  }
  if (series.fill === 'crossHatched') {
    return {
      color: CHART_COLOURS.surface,
      pattern: {
        shape: 'x',
        fgcolor: hue,
        bgcolor: CHART_COLOURS.surface,
        size: 5,
        solidity: 0.3,
      },
      line: outline,
    }
  }
  if (series.fill === 'light') {
    return { color: rgba(hue, 0.35), line: outline }
  }
  return { color: hue, line: { color: hue, width: 0 } }
}

export type FigureOptions = {
  range: [number, number]
  height: number
  planStart: Date | null
}

export const buildFigure = (
  { site, weeks }: SiteWalk,
  { range, height, planStart }: FigureOptions
): { data: Data[]; layout: Partial<Layout> } => {
  const points = weeks.flatMap((week) =>
    week.steps.map((step) => ({ week, step }))
  )
  // One numeric slot per (week, step), in walk order; the axis labels weeks
  // only (US154348).
  const x = points.map((_, j) => j)
  const stepsPerWeek = STEPS.length

  // An invisible bar filling each step's whole column alone carries the
  // hover, so pointing anywhere in a week shows that week's box.
  const hoverTexts = new Map(
    weeks.map((week) => [week, weekHoverText(week, planStart)])
  )
  const anchor: BarTrace = {
    type: 'bar',
    x,
    y: points.map(() => range[1] - range[0]),
    base: points.map(() => range[0]),
    width: 1,
    marker: { color: 'rgba(0,0,0,0)' },
    customdata: points.map(({ week }) => [hoverTexts.get(week) as string]),
    hovertemplate: '%{customdata[0]}<extra></extra>',
    showlegend: false,
  }
  const data: Data[] = [anchor as Data]

  SERIES.forEach((series) => {
    const bases: (number | null)[] = []
    const heights: (number | null)[] = []
    points.forEach(({ step }) => {
      const segment = step.segments.find(
        (s) => s.series.id === series.id && s.high - s.low > EPSILON
      )
      bases.push(segment ? segment.low : null)
      heights.push(segment ? segment.high - segment.low : null)
    })
    if (!heights.some((h) => h !== null)) return
    const trace: BarTrace = {
      type: 'bar',
      name: series.label,
      x,
      y: heights,
      base: bases,
      width: BAR_WIDTH,
      marker: markerFor(series),
      hoverinfo: 'skip',
      showlegend: false,
    }
    data.push(trace as Data)
  })

  const shapes: Partial<Shape>[] = []
  // Dotted connectors, edge to edge between consecutive steps (across week
  // boundaries too), behind the bars.
  for (let j = 0; j < points.length - 1; j++) {
    const y = points[j].step.end
    shapes.push({
      type: 'line',
      xref: 'x',
      yref: 'y',
      x0: j + BAR_WIDTH / 2,
      x1: j + 1 - BAR_WIDTH / 2,
      y0: y,
      y1: y,
      // A dash-length list, which Plotly takes but its types do not name.
      line: {
        color: CHART_COLOURS.muted,
        width: 1.2,
        dash: '1.5px,1.5px' as Dash,
      },
      layer: 'below',
    })
  }
  weeks.forEach((week, w) => {
    const first = w * week.steps.length
    const last = first + week.steps.length - 1
    // Safety stock (E1): one dashed segment across the week's four steps.
    shapes.push({
      type: 'line',
      xref: 'x',
      yref: 'y',
      x0: first - 0.5,
      x1: last + 0.5,
      y0: week.safetyStock,
      y1: week.safetyStock,
      line: { color: SAFETY_STOCK_COLOUR, width: 2, dash: 'dash' },
      layer: 'below',
    })
    // Solid week dividers, full plot height.
    if (w > 0) {
      shapes.push({
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: first - 0.5,
        x1: first - 0.5,
        y0: 0,
        y1: 1,
        line: { color: CHART_COLOURS.rule, width: 1 },
        layer: 'below',
      })
    }
  })

  const layout: Partial<Layout> = {
    title: {
      text: site,
      x: 0.05,
      xanchor: 'left',
      font: { size: 14 },
    },
    height,
    barmode: 'overlay',
    bargap: 0.22,
    showlegend: false,
    hovermode: 'closest',
    paper_bgcolor: CHART_COLOURS.surface,
    plot_bgcolor: CHART_COLOURS.surface,
    font: { family: FONT, size: 11, color: CHART_COLOURS.ink },
    hoverlabel: {
      bgcolor: CHART_COLOURS.surface,
      bordercolor: CHART_COLOURS.rule,
      font: { family: FONT, size: 12, color: CHART_COLOURS.ink },
      align: 'left',
      namelength: -1,
    },
    margin: { l: 64, r: 16, t: 25, b: 8 },
    xaxis: {
      type: 'linear',
      range: [-0.5, points.length - 0.5],
      tickmode: 'array',
      tickvals: weeks.map((_, w) => w * stepsPerWeek + (stepsPerWeek - 1) / 2),
      ticktext: weeks.map((week) => week.label),
      tickfont: { size: 10.5 },
      showgrid: false,
      zeroline: false,
      linecolor: CHART_COLOURS.rule,
      tickangle: 0,
      automargin: true,
    },
    yaxis: {
      range,
      gridcolor: CHART_COLOURS.grid,
      zerolinecolor: CHART_COLOURS.rule,
      tickformat: ',.0f',
      nticks: 6,
      automargin: true,
    },
    shapes,
  }
  return { data, layout }
}
